import React, { useState, useEffect, useCallback } from "react";
import { Switch, Route, useLocation, useHistory } from "react-router-dom";
import { connect } from "react-redux";
import Header from "./components/Header";
import Main from "./components/Main";
import Footer from "./components/Footer";
import Login from "./components/auth/Login";
import Onboarding from "./components/pages/Onboarding";
import JoinNetwork from "./components/pages/JoinNetwork";
import ForgotPassword from "./components/auth/ForgotPassword";
import Loader from "./components/Loader";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";
import "./App.sass";
import "font-awesome/css/font-awesome.min.css";
import "bootstrap-daterangepicker/daterangepicker.css";
import "react-datepicker/dist/react-datepicker.css";
import { store } from "./redux/store";
import { loadUser } from "./redux/action/login.action";
import {
  loadNetworksList,
  loadGroupsList,
} from "./redux/action/networks.action";
import axios from "axios";
import { loggedInTokenConfig, checkTokenConfig } from "./helpers/TokenConfig";
import GoToTop from "./components/GoToTop";
import { API_URL } from "./constants/Globals";
import useAxiosInterceptors from "./hooks/useAxiosInterceptor";
import { buySubscriptionPlan } from "./services/SubscriptionSvc";
import { getCookieDomain } from "./helpers/Session";

const App = (props) => {
  useAxiosInterceptors();
  const [isLoadingComplete, setLoadingComplete] = useState(false);

  const location = useLocation();
  const history = useHistory(); // ✅ React Router history
  const searchParams = new URLSearchParams(location.search);

  const token = searchParams.get("token");
  const refreshToken = searchParams.get("refreshToken");
  const refreshTokenExpireAt = searchParams.get("refreshTokenExpireAt");
  const tokenExpireAt = searchParams.get("tokenExpireAt");
  const currentUserId = searchParams.get("currentUserId");
  const planId = searchParams.get("planId");
  const groupId = searchParams.get("groupId");
  const billingCycle = searchParams.get("billingCycle");

  const redirectToStripeCheckout = async () => {
    try {
      const data = {
        subscriptionTypeId: planId,
        billingCycle: billingCycle,
        groupId: groupId,
      };
      const res = await buySubscriptionPlan(data);
      if (res.data.status === 1) {
        if (res.data.data?.url) {
          window.location.href = res.data.data.url;
        }
      }
    } catch (error) {
      console.error("Error upgrading subscription plan:", error);
      // setPaymentResult("failed");
    }
  };

  const handleAuthToken = useCallback(async () => {
    const isLocalhost =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";
    const domainAttr = isLocalhost ? "" : `; domain=.${getCookieDomain()}`;
    document.cookie = `sidedAccessToken=${token}; path=/${domainAttr}`;
    document.cookie = `sidedAccessTokenExpireAt=${tokenExpireAt}; path=/${domainAttr}`;
    document.cookie = `sidedAccessRefreshToken=${refreshToken}; path=/${domainAttr}`;
    document.cookie = `sidedAccessRefreshTokenExpireAt=${refreshTokenExpireAt}; path=/${domainAttr}`;
    document.cookie = `sidedCurrentUserId=${currentUserId}; path=/${domainAttr}`;
    if (planId && groupId && billingCycle) {
      redirectToStripeCheckout();
    } else {
      history.replace({
        pathname: location.pathname,
        search: "",
      });
      window.location.reload();
    }
  }, [token, refreshToken, refreshTokenExpireAt, tokenExpireAt, currentUserId]);

  useEffect(() => {
    if (
      token &&
      refreshToken &&
      refreshTokenExpireAt &&
      tokenExpireAt &&
      currentUserId
    ) {
      handleAuthToken();
    }
  }, [
    token,
    refreshToken,
    refreshTokenExpireAt,
    tokenExpireAt,
    currentUserId,
    handleAuthToken,
  ]);

  // console.log({ token, refreshToken, refreshTokenExpireAt, tokenExpireAt, currentUserId });
  useEffect(() => {
    setInterval(() => {
      axios
        .get(`${API_URL}/userClients`)
        .then(() => {})
        .catch((error) => {
          if (error.response.status === 401) {
            window.location.reload();
          }
        });
    }, 60000);
  }, []);

  useEffect(() => {
    if (
      !token &&
      !refreshToken &&
      !refreshTokenExpireAt &&
      !tokenExpireAt &&
      !currentUserId
    ) {
      if (isLoadingComplete) {
        if (props.isLogin) {
          store.dispatch(loadNetworksList());
          store.dispatch(loadGroupsList());
        } else {
          store.dispatch(loadUser());
        }
      } else {
        loadResourcesAsync(setLoadingComplete);
      }
    }
  }, [props.isLogin, props.isVerified, isLoadingComplete]);

  const { isLogin, isVerified, networks, user } = props;
  const { href } = window.location;

  if (!isLoadingComplete || props.isUserLoading) {
    return <Loader />;
  } else {
    return (
      <Switch>
        {props.isLogin &&
        props.isVerified &&
        href.indexOf("/join/") === -1 &&
        href.indexOf("/onboarding") === -1 ? (
          <div className="App">
            <GoToTop />
            <Header />
            <Main />
            <Footer />
          </div>
        ) : (
          <div className="App">
            <div className="authContainer">
              <Route
                exact
                path="/onboarding"
                render={(props) => (
                  <Onboarding
                    {...props}
                    isLogin={isLogin}
                    isVerified={isVerified}
                    networks={networks}
                    user={user}
                    refreshClients={() => store.dispatch(loadNetworksList())}
                  />
                )}
              />
              <Route
                exact
                path="/admin-settings/network-users/join/:token"
                render={(props) => <JoinNetwork {...props} isLogin={isLogin} />}
              />
              <Route exact path="/forgot-password" component={ForgotPassword} />
              <Route path="/" component={Login} />
            </div>
          </div>
        )}
      </Switch>
    );
  }
};

axios.interceptors.request.use(async (config) => {
  return await loggedInTokenConfig(config);
});

async function loadResourcesAsync(setLoadingComplete) {
  await checkTokenConfig();
  setLoadingComplete(true);
}

const mapStateToProps = (state) => ({
  isLogin: state.login.isLogin,
  isVerified: state.login.isVerified,
  isUserLoading: state.login.isLoading,
  isNetworksLoading: state.networks.isLoading,
  isDebatesLoading: state.debates.isLoading,
  isUsersLoading: state.networkUsers.isLoading,
  loginError: state.login.loginError,
  networks: state.networks.networksList,
  user: state.login.user,
  selLanguage: state.networks.selLanguage,
});

export default connect(mapStateToProps)(App);
